import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ActionIcon, Button, Menu } from '@mantine/core';
import { Copy, Dots, Pencil, Plus, Stack, Trash } from 'tabler-icons-react';
import { Context as StoreContext } from '../../../providers/StoreContextProvider';
import { Context as AuthContext } from '../../../providers/AuthContextProvider';
import TableView from '../../common/TableView';
import { triggerNotification } from '../../../helpers/notification';
import HeaderView from '../../common/HeaderView';
import AppStack from '../../common/AppStack';
import AppText from '../../common/AppText';
import AppMenu from '../../common/AppMenu';
import {
  ECOM_PRODUCT_BUNDLE_TYPE_LIST,
  ECOM_PRODUCT_STATUS_ENUM,
  VIEW_ACTIONS_ENUM
} from '../../../config/constants';
import { useModalState, usePaginationFilter } from '../../../helpers/hooks';
import ConfirmModal from '../../common/ConfirmModal';

const TABLE_COLUMNS = [
  {
    label: 'Name',
    value: 'name',
    sortable: true
  },
  {
    label: 'Status',
    value: 'status',
    sortable: true
  },
  {
    label: 'Bundle type',
    value: 'bundle_type',
    sortable: true
  },
  {
    label: 'Products',
    value: 'products',
    sortable: true
  }
];

const VIEW_ACTIONS = [
  {
    label: 'Edit',
    value: VIEW_ACTIONS_ENUM.EDIT,
    section: 1,
    icon: <Pencil size={18} />
  },
  {
    label: 'Duplicate',
    value: VIEW_ACTIONS_ENUM.DUPLICATE,
    section: 1,
    icon: <Copy size={18} />,
    isVisible: ({ data }) =>
      data.fkEcomStore === data.ecomVendorBundle.ecomStore?.pkEcomStore
  },
  {
    label: 'Delete',
    value: VIEW_ACTIONS_ENUM.DELETE,
    icon: <Trash color="red" size={18} />,
    section: 2,
    color: 'red'
  }
];

const StoreBundleTable = () => {
  const fetchedPk = useRef(null);
  const navigate = useNavigate();
  const { state: authState } = useContext(AuthContext);
  const {
    state,
    fetchPaginatedStoreProductPackages,
    deleteStoreProductPackages
  } = useContext(StoreContext);
  const {
    state: modalState,
    onOpenModal,
    onCloseModal,
    onChangeModalLoading
  } = useModalState();
  const [selectedRows, setSelectedRows] = useState([]);
  const {
    pageIndex,
    totalCount,
    totalPages,
    totalNoFilterCount,
    data,
    filter,
    loading: dataLoading,
    isDescendingSort,
    onPageChange,
    onFilterChange,
    onSortChange,
    onRefresh
  } = usePaginationFilter(
    {
      search: '',
      fkEcomCategory: null,
      fkEcomProductStatus: null
    },
    (newFilter) =>
      fetchPaginatedStoreProductPackages(authState.pkEcomStore, newFilter),
    state.paginatedEcomStoreProductPackages
  );

  const loading = !fetchedPk.current || dataLoading;
  const noBundlesCreated = !loading && totalNoFilterCount === 0;
  const hasFiltered =
    filter.search || filter.status || filter.owner || filter.vendorName;

  useEffect(() => {
    if (authState.pkEcomStore) {
      onRefresh();
      fetchedPk.current = authState.pkEcomStore;
    }
  }, [authState.pkEcomStore]);

  return (
    <HeaderView
      breadcrumbs={[
        {
          title: 'Product bundles',
          to: '/merchant/product-bundles'
        }
      ]}
      rightSection={
        !loading && (
          <Button
            color="dark"
            leftSection={<Plus size={18} />}
            onClick={() => navigate('/merchant/product-bundles/new')}
            radius="md"
            size="compact-md"
            style={{ fontSize: 14 }}
          >
            Create bundle
          </Button>
        )
      }
    >
      <TableView
        columns={TABLE_COLUMNS}
        disabled={noBundlesCreated && !hasFiltered}
        disabledContent={{
          title: 'Create your first bundle',
          description:
            'Product bundles are groups of products that can be sold together.',
          icon: <Stack color="#000" size={125} />,
          primaryButton: {
            label: 'Create product bundle',
            icon: <Plus size={18} />,
            link: '/merchant/product-bundles/new'
          }
        }}
        emptyMessage="No product bundles found."
        enableInputsWhenLoading
        filters={[
          {
            key: 1,
            label: 'Search',
            placeholder: 'Filter by name',
            value: filter.search,
            onChange: (value) => onFilterChange({ ...filter, search: value })
          },
          {
            key: 2,
            label: 'Status',
            type: 'select',
            value: filter.status || null,
            placeholder: 'Filter by status',
            searchable: false,
            clearable: true,
            data: [
              { value: 'ACTIVE', label: 'Active' },
              { value: 'DRAFT', label: 'Draft' },
              { value: 'UNAVAILABLE', label: 'Unavailable' },
              { value: 'REQUIRES_ACTION', label: 'Requires Action' }
            ],
            onChange: (value) => {
              onFilterChange(
                {
                  ...filter,
                  status: value
                },
                true
              );
            }
          },
          {
            key: 3,
            label: 'Owner',
            type: 'select',
            value: filter.owner || null,
            placeholder: 'Filter by owner',
            searchable: false,
            clearable: true,
            data: [
              { value: 'vendor', label: 'Vendor owned' },
              { value: 'owned_by_me', label: 'Owned by me' }
            ],
            onChange: (value) => {
              onFilterChange(
                {
                  ...filter,
                  owner: value
                },
                true
              );
            }
          },
          {
            key: 4,
            label: 'Vendor',
            placeholder: 'Filter by vendor',
            value: filter.vendorName || '',
            onChange: (value) =>
              onFilterChange({ ...filter, vendorName: value })
          }
        ]}
        isDescendingSort={isDescendingSort}
        isLoading={loading}
        noSort
        onAction={(action, item) => {
          switch (action) {
            case VIEW_ACTIONS_ENUM.EDIT:
              navigate(`/merchant/product-bundles/${item.key}`);
              break;
            case VIEW_ACTIONS_ENUM.DUPLICATE:
              navigate(
                `/merchant/product-bundles/${item.key}/duplicate?from=product-bundles`
              );
              break;
            default:
              onOpenModal(action, [
                data.find((b) => b.pkEcomStoreProductPackage === item.key)
              ]);
              break;
          }
        }}
        onChangeSortBy={onSortChange}
        onPageChange={onPageChange}
        onRefresh={onRefresh}
        onSelect={setSelectedRows}
        pageIndex={pageIndex}
        rows={data.map((p) => {
          const hasUnavailableProducts =
            p.ecomVendorBundle.deleted ||
            p.ecomVendorBundle.ecomVendorBundleProducts.some(
              (pb) =>
                pb.productDeleted ||
                pb.fkEcomProductStatus.toString() !==
                  ECOM_PRODUCT_STATUS_ENUM.ACTIVE
            );

          const isMissingStoreProducts =
            p.ecomVendorBundle.ecomVendorBundleProducts.length !==
            p.ecomStoreProducts.length;

          return {
            key: p.pkEcomStoreProductPackage,
            actions: VIEW_ACTIONS.filter(
              (a) => !a.isVisible || a.isVisible({ data: p })
            ),
            data: p,
            columns: [
              {
                key: 1,
                label: p.ecomVendorBundle.name,
                to: `/merchant/product-bundles/${p.pkEcomStoreProductPackage}`,
                component: Link,
                weight: 500,
                src:
                  p.ecomVendorBundle.previewImage ||
                  p.ecomVendorBundle.ecomVendorBundleProducts
                    .map((f) => f.previewImage)
                    .find(Boolean) ||
                  null,
                showImage: true,
                subLabel:
                  !p.ecomVendorBundle.ecomStore ||
                  p.ecomVendorBundle.ecomStore.pkEcomStore !== p.fkEcomStore
                    ? p.ecomVendorBundle.ecomVendor.name
                    : ''
              },
              {
                key: 2,
                ...(hasUnavailableProducts
                  ? {
                      label: 'Unavailable',
                      color: '#C40000'
                    }
                  : isMissingStoreProducts
                  ? {
                      label: 'Requires action',
                      color: '#C40000'
                    }
                  : p.fkEcomProductStatus.toString() ===
                    ECOM_PRODUCT_STATUS_ENUM.ACTIVE
                  ? {
                      label: 'Active',
                      color: 'green'
                    }
                  : {
                      label: 'Draft',
                      color: 'grey'
                    })
              },
              {
                key: 3,
                label:
                  ECOM_PRODUCT_BUNDLE_TYPE_LIST.find(
                    (t) =>
                      t.value === p.ecomVendorBundle.fkEcomBundleType.toString()
                  )?.label || 'Unknown type'
              },
              {
                key: 4,
                label: p.ecomVendorBundle.ecomVendorBundleProducts.length
              }
            ]
          };
        })}
        selectActionSection={
          <AppMenu
            control={
              <ActionIcon color="#ced4da" size="lg" variant="outline">
                <Dots color="#000" />
              </ActionIcon>
            }
            styles={{ dropdown: { padding: 0 } }}
          >
            <AppStack style={{ padding: 2, gap: 1 }}>
              <Menu.Item
                leftSection={<Trash size={18} />}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onOpenModal(
                    VIEW_ACTIONS_ENUM.DELETE,
                    data.filter((b) =>
                      selectedRows.includes(b.pkEcomStoreProductPackage)
                    )
                  );
                }}
                style={{ color: 'red' }}
              >
                Delete packages
              </Menu.Item>
            </AppStack>
          </AppMenu>
        }
        selectedRows={selectedRows}
        sortBy={filter.sort || TABLE_COLUMNS[0].value}
        tableTitle="Product Bundles"
        totalCount={totalCount}
        totalPages={totalPages}
      />

      <ConfirmModal
        confirmActionColor="red"
        confirmActionText="Yes, delete bundles"
        isLoading={modalState.loading}
        isOpen={
          modalState.isOpen && modalState.action === VIEW_ACTIONS_ENUM.DELETE
        }
        message={
          <AppStack style={{ gap: 10 }}>
            <AppText style={{ fontSize: 16 }}>
              Are you sure you want to delete{' '}
              <b>
                {modalState.item?.length === 1
                  ? modalState.item[0]?.ecomVendorBundle.name
                  : `${modalState.item?.length} product bundles`}
              </b>
              ?
            </AppText>
            <AppText style={{ fontSize: 14, fontWeight: 500 }}>
              This action cannot be undone.
            </AppText>
          </AppStack>
        }
        onCancel={onCloseModal}
        onConfirm={() => {
          onChangeModalLoading(true);
          deleteStoreProductPackages(
            authState.pkEcomStore,
            modalState.item.map((i) => i.pkEcomStoreProductPackage),
            () => {
              triggerNotification(
                modalState.item.length === 1
                  ? 'Product bundle deleted.'
                  : 'Product bundles deleted.',
                'success'
              );
              onCloseModal();
              onRefresh();
            },
            (error) => {
              triggerNotification(error);
              onChangeModalLoading(false);
            }
          );
        }}
        title={`Delete ${
          modalState.item?.length === 1
            ? modalState.item[0]?.name
            : `${modalState.item?.length} product bundles`
        }?
                `}
      />
    </HeaderView>
  );
};

StoreBundleTable.propTypes = {};

export default StoreBundleTable;
